import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { CommonState, error, isFetching } from '@pec/aion-ui-core/slices/common';
import { GetDataSharingConsent, PostDataSharingConsent } from '../types/dataSharingConsent';
import { enqueueRequestErrorSnackbar } from '@pec/aion-ui-core/actions/enqueueSnackbar';
import { CallbackAllowList, PartnerIdToUrlMap } from '../constants/dataSharingConsent';

const name = 'dataSharingConsent';

export const fetchAllDataSharingConsent = createAsyncThunk(
  `${name}/fetch`,
  async (organizationId: string, { dispatch }) => {
    try {
      const { data } = await axios.get<GetDataSharingConsent[]>(
        `/api/v1/third-party-integrations/organizations/${organizationId}`
      );
      return data;
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export const postDataSharingConsent = createAsyncThunk(`${name}/post`, async (data: any, { dispatch }) => {
  const {
    cproOrganizationId,
    callbackUrl,
    thirdPartyOrganizationId,
    shouldRedirect,
    isDataSharing,
    externalOrganizationName,
    externalRegistrationId,
    userId,
    name,
    email,
    phone
  } = data;

  const payload: PostDataSharingConsent = {
    thirdPartyOrganizationId,
    isDataSharing,
    shouldRedirect,
    externalOrganizationName,
    externalRegistrationId,
    userId,
    name,
    email,
    phone
  };

  const queryParams: URLSearchParams = new URLSearchParams([
    ['registrationId', payload.externalRegistrationId],
    ['organizationId', cproOrganizationId]
  ]);

  try {
    const response = await axios.post<PostDataSharingConsent>(
      `/api/v1/third-party-integrations/organizations/${cproOrganizationId}`,
      payload
    );

    const redirectUrl = callbackUrl ?? PartnerIdToUrlMap.get(payload.thirdPartyOrganizationId);

    const domainAllowList = CallbackAllowList.some((el: string) => !!redirectUrl.includes(el));

    if (!shouldRedirect || !domainAllowList) return;

    window.open(`${redirectUrl}?${queryParams.toString()}`, '_blank', 'noopener,noreferrer');

    return response.data;
  } catch (error) {
    dispatch(enqueueRequestErrorSnackbar(error.message));
    throw error;
  } finally {
    dispatch(fetchAllDataSharingConsent(cproOrganizationId));
  }
});

export type State = CommonState & {
  dataSharingConsent: GetDataSharingConsent[];
};

const initialState: State = {
  dataSharingConsent: [],
  isFetching: false,
  error: null
};

const dataSharingConsent = createSlice({
  name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAllDataSharingConsent.pending, isFetching)
      .addCase(fetchAllDataSharingConsent.fulfilled, (state, action) => {
        state.dataSharingConsent = action.payload;
        state.isFetching = false;
      })
      .addCase(fetchAllDataSharingConsent.rejected, error)
      .addCase(postDataSharingConsent.rejected, error)
      .addCase(postDataSharingConsent.pending, isFetching)
      .addCase(postDataSharingConsent.fulfilled, state => {
        state.isFetching = false;
      });
  }
});

export const { reducer: dataSharingConsentReducer } = dataSharingConsent;
